import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { resizeImage, COVER_SIZE, LOGO_SIZE } from "@/lib/imageTools";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ImagePlus } from "lucide-react";

const GROUPS = [
  {
    title: "Company",
    fields: [
      { key: "company_name", label: "Company Name" },
      { key: "tagline", label: "Tagline" },
      { key: "logo", label: "Logo", type: "image" },
    ],
  },
  {
    title: "Contact",
    fields: [
      { key: "phone", label: "Primary Phone" },
      { key: "phone2", label: "Secondary Phone" },
      { key: "whatsapp", label: "WhatsApp Number (digits only, with country code)" },
      { key: "email", label: "Email" },
      { key: "address", label: "Full Address", type: "textarea" },
    ],
  },
  {
    title: "Social Media",
    fields: [
      { key: "instagram", label: "Instagram URL" },
      { key: "facebook", label: "Facebook URL" },
      { key: "linkedin", label: "LinkedIn URL" },
      { key: "youtube", label: "YouTube URL" },
      { key: "x", label: "X (Twitter) URL" },
    ],
  },
  {
    title: "SEO",
    fields: [
      { key: "seo_title", label: "Website Title" },
      { key: "seo_description", label: "Meta Description", type: "textarea" },
      { key: "seo_keywords", label: "Keywords (comma separated)" },
      { key: "seo_og_image", label: "OG Image", type: "image" },
    ],
  },
  {
    title: "Footer",
    fields: [
      { key: "footer_copyright", label: "Copyright Line" },
      { key: "footer_description", label: "Footer Description", type: "textarea" },
    ],
  },
];

export function SettingsModule() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved — live on the website");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-module-settings" className="max-w-3xl space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold text-white">Website Settings</h2>
        <p className="text-sm text-slate-400">Company, contact, social, SEO and footer content. Changes go live immediately.</p>
      </div>
      {GROUPS.map((g) => (
        <div key={g.title} className="glass rounded-xl p-6 space-y-4" data-testid={`settings-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
          <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#FF8800] uppercase">{g.title}</p>
          {g.fields.map((f) => (
            <div key={f.key} className="space-y-2">
              <Label className="text-slate-300">{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea
                  data-testid={`settings-field-${f.key}`}
                  rows={3}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  className="bg-[#06080E] border-white/10"
                />
              ) : f.type === "image" ? (
                <div className="flex items-start gap-4">
                  <label className="flex flex-col items-center justify-center w-32 h-20 rounded-lg border border-dashed border-white/20 bg-[#06080E] cursor-pointer hover:border-[#FF8800]/60 transition-colors overflow-hidden shrink-0" data-testid={`settings-field-${f.key}-upload`}>
                    {form[f.key] ? (
                      <img src={form[f.key]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="flex flex-col items-center">
                        <ImagePlus className="w-5 h-5 text-slate-500" />
                        <span className="text-[9px] text-slate-500 mt-1">auto-fit {f.key === "logo" ? "600×600" : "1200×630"}</span>
                      </span>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
                        const spec = f.key === "logo" ? LOGO_SIZE : COVER_SIZE;
                        try {
                          const resized = await resizeImage(file, spec);
                          setForm((p) => ({ ...p, [f.key]: resized }));
                          toast.success(`Image adjusted to ${spec.width}×${spec.height}`);
                        } catch { toast.error("Could not read that image"); }
                      }}
                    />
                  </label>
                  <Input
                    data-testid={`settings-field-${f.key}`}
                    value={(form[f.key] || "").startsWith("data:") ? "" : form[f.key] ?? ""}
                    onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                    placeholder="...or paste image URL"
                    className="bg-[#06080E] border-white/10 text-xs flex-1"
                  />
                </div>
              ) : (
                <Input
                  data-testid={`settings-field-${f.key}`}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                  className="bg-[#06080E] border-white/10"
                />
              )}
            </div>
          ))}
        </div>
      ))}
      <Button onClick={save} disabled={saving} data-testid="settings-save" className="bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold h-11 px-8">
        {saving ? "Saving..." : "Save All Settings"}
      </Button>
    </div>
  );
}
